// /cameras/:locationId — single-location viewer.
//
// Layout:
//   Left (main):    <WhepPlayer> for the currently-selected camera.
//                   Role-gated action row below (download, tag incident).
//   Right (aside):  thumbnail grid of sibling cameras at the location, plus
//                   a recent-incidents list sourced from /api/cameras/{id}/events.
//
// A ?camera=<id> search param selects which camera to show as primary;
// when absent it defaults to the first online camera at the location, falling
// back to the first listed. A ?camera= that names a device absent from the
// authorized list shows a not-found/no-access banner rather than silently
// rendering a different camera.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import {
  Button,
  LegacyCameraTile,
  StatusChip,
  useToast,
} from "@/components";
import { WhepPlayer } from "@/components/WhepPlayer";
import { useRole } from "@/role";
import { capabilitiesFor } from "@/role/capabilities";
import { useSession } from "@/session";
import { ApiError } from "@/api";
import {
  cameraLocationsClient,
  type CameraDomainLocation,
} from "@/api/cameraLocationsClient";
import { camerasClient, type Camera } from "@/api/camerasClient";
import { eventsClient, type CameraEvent } from "@/api/eventsClient";
import { adaptCameraForTile } from "@/utils/cameraAdapter";
import { resolvePrimaryCamera } from "./resolvePrimaryCamera";
import "./Cameras.css";

function describeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return `${err.status} ${err.code ?? err.message}`;
  if (err instanceof Error) return err.message;
  return fallback;
}

function formatUtc(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
}

function deriveCameraName(cam: Camera): string {
  const desc = cam.description?.trim();
  if (desc) return desc;
  const tail = cam.doorbot_id.slice(-6).toUpperCase();
  return `Camera ${tail}`;
}

function statusToneFor(status: string): "success" | "warning" | "danger" | "info" {
  if (status === "online") return "success";
  if (status === "degraded") return "warning";
  if (status === "offline") return "danger";
  return "info";
}

interface CameraAboutProps {
  camera: Camera;
  locationDisplayName: string;
}

function CameraAbout({ camera, locationDisplayName }: CameraAboutProps): JSX.Element {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(camera.doorbot_id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard may be unavailable (e.g. http or sandboxed iframe)
    }
  }, [camera.doorbot_id]);

  const friendlyName = deriveCameraName(camera);
  const kindLabel = camera.kind ?? "ring camera";

  return (
    <dl className="camera-about">
      <div className="camera-about__row">
        <dt className="camera-about__label">Name</dt>
        <dd className="camera-about__value">
          <span className="camera-about__name">{friendlyName}</span>
          <StatusChip tone={statusToneFor(camera.status)}>
            {camera.status}
          </StatusChip>
        </dd>
      </div>
      <div className="camera-about__row">
        <dt className="camera-about__label">Type</dt>
        <dd className="camera-about__value">{kindLabel}</dd>
      </div>
      <div className="camera-about__row">
        <dt className="camera-about__label">Location</dt>
        <dd className="camera-about__value">{locationDisplayName}</dd>
      </div>
      {camera.last_seen_at && (
        <div className="camera-about__row">
          <dt className="camera-about__label">Last seen</dt>
          <dd className="camera-about__value">{formatUtc(camera.last_seen_at)}</dd>
        </div>
      )}
      {camera.last_motion_at && (
        <div className="camera-about__row">
          <dt className="camera-about__label">Last motion</dt>
          <dd className="camera-about__value">{formatUtc(camera.last_motion_at)}</dd>
        </div>
      )}
      <div className="camera-about__row">
        <dt className="camera-about__label">Device ID</dt>
        <dd className="camera-about__value">
          <button
            type="button"
            className="camera-about__copy"
            onClick={handleCopy}
            title={copied ? "Copied!" : "Copy device ID"}
            aria-label={`Copy device ID ${camera.doorbot_id}`}
          >
            <code className="camera-about__id">{camera.doorbot_id}</code>
            <span className="camera-about__copy-hint" aria-hidden="true">
              {copied ? "✓" : "⎘"}
            </span>
          </button>
        </dd>
      </div>
    </dl>
  );
}

function eventTimestamp(ev: CameraEvent): string | null {
  // start_ms is the canonical event start; AVA sometimes returns 0 today,
  // in which case we fall back to created_at, then null.
  if (ev.start_ms && ev.start_ms > 0) {
    return new Date(ev.start_ms).toISOString();
  }
  if (ev.created_at) return ev.created_at;
  return null;
}

type LocationsState =
  | { kind: "loading" }
  | { kind: "ready"; location: CameraDomainLocation | null }
  | { kind: "error"; message: string };

type CamerasState =
  | { kind: "loading" }
  | { kind: "ready"; cameras: Camera[] }
  | { kind: "error"; message: string };

type EventsState =
  | { kind: "loading" }
  | { kind: "ready"; events: CameraEvent[] }
  | { kind: "error"; message: string };

export function CameraDetailPage(): JSX.Element {
  const { locationId } = useParams<{ locationId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentRole } = useRole();
  const { session } = useSession();
  const toast = useToast();

  const caps = useMemo(() => capabilitiesFor(currentRole), [currentRole]);

  const [locState, setLocState] = useState<LocationsState>({ kind: "loading" });
  const [camState, setCamState] = useState<CamerasState>({ kind: "loading" });
  const [evState, setEvState] = useState<EventsState>({ kind: "loading" });

  // Load the location summary (name, address) from the locations list. We
  // could also fetch /api/locations/{id} if it exists, but the list call
  // is already cached behind the BFF and gives us address + counts.
  useEffect(() => {
    // Wait for RoleProvider to restore X-Acting-Role on hard refresh; firing
    // /api/* before the role is set trips the missingRoleHandler in
    // client.ts and forces a /signin redirect (looks like an unwanted logout).
    if (!locationId || currentRole === null) return;
    const ctrl = new AbortController();
    void (async () => {
      try {
        const all = await cameraLocationsClient.list(ctrl.signal);
        const match = all.find((l) => l.location_id === locationId) ?? null;
        setLocState({ kind: "ready", location: match });
      } catch (err) {
        if (ctrl.signal.aborted) return;
        setLocState({
          kind: "error",
          message: describeError(err, "Failed to load location"),
        });
      }
    })();
    return () => ctrl.abort();
  }, [locationId, currentRole]);

  // Load cameras at this location.
  useEffect(() => {
    if (!locationId || currentRole === null) return;
    const ctrl = new AbortController();
    void (async () => {
      try {
        const cameras = await camerasClient.list({
          locationId,
          signal: ctrl.signal,
        });
        setCamState({ kind: "ready", cameras });
      } catch (err) {
        if (ctrl.signal.aborted) return;
        setCamState({
          kind: "error",
          message: describeError(err, "Failed to load cameras"),
        });
      }
    })();
    return () => ctrl.abort();
  }, [locationId, currentRole]);

  // Authorization check — derive from the v0.2 session.effective_rules
  // (deny-wins, two-axis ALL/ALLOW/DENY scope) and fall back on the legacy
  // session.grants list for sessions seeded by the old shape. The server is
  // authoritative; this just gives us a useful banner when the user clicks
  // through to a site they have no rule on.
  const hasAccess = useMemo(() => {
    const role = (currentRole ?? "").toLowerCase();
    if (locationId === null || locationId === undefined) return false;

    // v0.2 effective-rules predicate: walk every rule the session carries,
    // applying location-axis ALL / ALLOW / DENY semantics. Deny-wins.
    const rules = session?.effective_rules ?? [];
    if (rules.length > 0) {
      let sawAllow = false;
      for (const r of rules) {
        if ((r.role_id ?? "").toLowerCase() !== role) continue;
        const locScope = r.location_scope;
        if (!locScope) continue;
        const ids = locScope.ids ?? [];
        if (locScope.mode === "DENY" && ids.includes(locationId)) return false;
        if (
          locScope.mode === "ALL" ||
          (locScope.mode === "ALLOW" && ids.includes(locationId))
        ) {
          sawAllow = true;
        }
      }
      if (sawAllow) return true;
    }

    // Legacy fallback: pre-v0.2 sessions that still carry session.grants.
    const upperRole = role.toUpperCase();
    if (upperRole === "TENANT_ADMIN" || upperRole === "FLEET_ADMIN") {
      const fleetWide = (session?.grants ?? []).some(
        (g) =>
          (g.role ?? "").toUpperCase() === upperRole &&
          (g.location_id === "fleet-all" ||
            g.location_id?.startsWith("fleet-")),
      );
      if (fleetWide) return true;
    }
    return (session?.grants ?? []).some(
      (g) =>
        (g.role ?? "").toUpperCase() === upperRole &&
        g.location_id === locationId,
    );
  }, [currentRole, session, locationId]);

  const cameras = camState.kind === "ready" ? camState.cameras : [];

  const selectedCameraId = searchParams.get("camera");
  const { camera: primaryCamera, requestedMissing: requestedCameraMissing } =
    useMemo(
      () => resolvePrimaryCamera(cameras, selectedCameraId),
      [cameras, selectedCameraId],
    );

  const visibleSiblings = useMemo<Camera[]>(() => {
    if (!primaryCamera) return [];
    if (!caps.canViewMultiCamera) return [];
    return cameras.filter((c) => c.doorbot_id !== primaryCamera.doorbot_id);
  }, [cameras, primaryCamera, caps.canViewMultiCamera]);

  // Load incidents for the primary camera. We refetch on primary change so
  // the sidebar reflects the camera the user is looking at.
  useEffect(() => {
    if (!primaryCamera) {
      setEvState({ kind: "ready", events: [] });
      return;
    }
    if (currentRole === null) return;
    const ctrl = new AbortController();
    setEvState({ kind: "loading" });
    void (async () => {
      try {
        // Default 7-day window for the sidebar incident list.
        const end = new Date();
        const start = new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000);
        const res = await eventsClient.listForCamera(primaryCamera.doorbot_id, {
          start: start.toISOString(),
          end: end.toISOString(),
          limit: 25,
          signal: ctrl.signal,
        });
        setEvState({ kind: "ready", events: res.items ?? [] });
      } catch (err) {
        if (ctrl.signal.aborted) return;
        setEvState({
          kind: "error",
          message: describeError(err, "Failed to load incidents"),
        });
      }
    })();
    return () => ctrl.abort();
  }, [primaryCamera, currentRole]);

  const selectCamera = useCallback(
    (camId: string) => {
      const next = new URLSearchParams(searchParams);
      next.set("camera", camId);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Sync ?camera= with the resolved primary so bookmarks are deterministic.
  useEffect(() => {
    if (primaryCamera && !selectedCameraId) {
      const next = new URLSearchParams(searchParams);
      next.set("camera", primaryCamera.doorbot_id);
      setSearchParams(next, { replace: true });
    }
  }, [primaryCamera, selectedCameraId, searchParams, setSearchParams]);

  if (!locationId) {
    return (
      <section className="cameras-page">
        <header className="page-header">
          <h1 className="page-title">Location not found</h1>
          <p className="page-subtitle">
            <Link to="/cameras">Back to all cameras</Link>.
          </p>
        </header>
      </section>
    );
  }

  if (locState.kind === "loading") {
    return (
      <section className="cameras-page">
        <header className="page-header">
          <h1 className="page-title">Loading location…</h1>
        </header>
        <div className="cameras-page__banner">
          Looking up <code>{locationId}</code>…
        </div>
      </section>
    );
  }

  if (locState.kind === "error") {
    return (
      <section className="cameras-page">
        <header className="page-header">
          <h1 className="page-title">Failed to load location</h1>
        </header>
        <div className="cameras-page__banner cameras-page__banner--warn">
          <strong>Failed to load location.</strong> {locState.message}
        </div>
      </section>
    );
  }

  const location = locState.location;
  if (!location) {
    return (
      <section className="cameras-page">
        <header className="page-header">
          <h1 className="page-title">Location not found</h1>
          <p className="page-subtitle">
            No location with id <code>{locationId}</code>.{" "}
            <Link to="/cameras">Back to all cameras</Link>.
          </p>
        </header>
      </section>
    );
  }

  if (!hasAccess) {
    return (
      <section className="cameras-page">
        <header className="page-header">
          <h1 className="page-title">{location.display_name}</h1>
          <p className="page-subtitle">{location.address ?? ""}</p>
        </header>
        <div className="cameras-page__banner cameras-page__banner--warn">
          <strong>No access at this location under your current role.</strong>{" "}
          Active role: <code>{currentRole ?? "—"}</code>. Switch roles from
          the top-right if you hold a grant here, or return to{" "}
          <Link to="/cameras">the camera wall</Link>.
        </div>
      </section>
    );
  }

  const handleDownload = (): void => {
    if (!primaryCamera) return;
    toast.success({
      title: `Download requested for ${primaryCamera.description ?? primaryCamera.doorbot_id}`,
      description:
        "Download is not wired yet — backend MP4 download endpoint TBD.",
    });
  };

  const incidentEvents =
    evState.kind === "ready"
      ? evState.events.filter((e) => e.has_recording || e.person_detected)
      : [];

  return (
    <section className="cameras-page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{location.display_name}</h1>
          <p className="page-subtitle">
            {location.address ?? "Address unknown"} ·{" "}
            <code className="mono">{location.location_id}</code>
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Link to="/cameras">
            <Button variant="secondary" size="sm">
              All sites
            </Button>
          </Link>
          <Link
            to={`/recordings?location=${encodeURIComponent(location.location_id)}`}
          >
            <Button variant="secondary" size="sm">
              Past recordings
            </Button>
          </Link>
        </div>
      </header>

      {camState.kind === "loading" && (
        <div className="cameras-page__banner">Loading cameras…</div>
      )}
      {camState.kind === "error" && (
        <div className="cameras-page__banner cameras-page__banner--warn">
          <strong>Failed to load cameras for this site.</strong>{" "}
          {camState.message}
        </div>
      )}
      {camState.kind === "ready" && cameras.length === 0 && (
        <div className="cameras-page__banner cameras-page__banner--warn">
          <strong>No cameras at this location yet.</strong> Ring syncs the
          device catalog asynchronously after install — check back shortly.
        </div>
      )}
      {camState.kind === "ready" && requestedCameraMissing && (
        <div className="cameras-page__banner cameras-page__banner--warn">
          <strong>
            That camera wasn't found or isn't accessible under your current
            role.
          </strong>{" "}
          Active role: <code>{currentRole ?? "—"}</code>. View{" "}
          <Link to={`/cameras/${locationId}`}>this site's other cameras</Link>{" "}
          or return to <Link to="/cameras">the camera wall</Link>.
        </div>
      )}

      {primaryCamera && (
        <div className="camera-detail">
          <div className="camera-detail__main">
            <WhepPlayer deviceId={primaryCamera.doorbot_id} />
            <div
              className="player__actions"
              style={{ display: "flex", gap: 8, marginTop: 8 }}
            >
              {caps.canDownload && (
                <Button variant="primary" size="sm" onClick={handleDownload}>
                  Download clip
                </Button>
              )}
              {!caps.canDownload && (
                <StatusChip tone="info">View-only role</StatusChip>
              )}
            </div>

            <div className="camera-detail__section">
              <h2 className="camera-detail__section-title">
                About this camera
              </h2>
              <CameraAbout
                camera={primaryCamera}
                locationDisplayName={location.display_name}
              />
            </div>
          </div>

          <aside className="camera-detail__sidebar">
            {caps.canViewMultiCamera && visibleSiblings.length > 0 && (
              <div className="camera-detail__section">
                <h2 className="camera-detail__section-title">
                  Other cameras at this site
                </h2>
                <div className="camera-detail__thumbs">
                  {visibleSiblings.map((cam) => (
                    <div
                      key={cam.doorbot_id}
                      onClick={() => selectCamera(cam.doorbot_id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          selectCamera(cam.doorbot_id);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      style={{ cursor: "pointer" }}
                    >
                      <LegacyCameraTile
                        camera={adaptCameraForTile(cam, location.display_name)}
                        size="sm"
                        clickable={false}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="camera-detail__section">
              <h2 className="camera-detail__section-title">Recent activity</h2>
              {evState.kind === "loading" && (
                <p className="admin-muted" style={{ margin: 0 }}>
                  Loading recent events…
                </p>
              )}
              {evState.kind === "error" && (
                <p className="admin-muted" style={{ margin: 0 }}>
                  Failed to load events. {evState.message}
                </p>
              )}
              {evState.kind === "ready" && incidentEvents.length === 0 && (
                <p className="admin-muted" style={{ margin: 0 }}>
                  No recent activity at this camera.
                </p>
              )}
              {evState.kind === "ready" && incidentEvents.length > 0 && (
                <ul className="camera-detail__incident-list">
                  {incidentEvents.slice(0, 8).map((e) => {
                    const ts = eventTimestamp(e);
                    return (
                      <li
                        key={e.ding_id}
                        className="camera-detail__incident-item"
                      >
                        <div>
                          {e.kind ?? "Event"}
                          {e.person_detected ? " · person detected" : ""}
                        </div>
                        <div className="camera-detail__incident-time">
                          {ts ? formatUtc(ts) : "Unknown time"}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </aside>
        </div>
      )}
    </section>
  );
}
